import { getLastKnownAuthUser } from "./authService";

export type PendingMasterDataKind = "material" | "allocation" | "ownership";
export type PendingMasterDataOperation = "created" | "updated" | "deleted";
export type PendingMasterDataSyncState = "pending" | "conflict" | "error";

export interface PendingMasterDataMutation {
  key: string;
  owner_user_id: number;
  kind: PendingMasterDataKind;
  identity_key: string;
  target: string | number | null;
  operation: PendingMasterDataOperation;
  payload: Record<string, unknown> | null;
  baseline_data: Record<string, unknown> | null;
  draft_revision: number | null;
  master_version: number | null;
  created_at: string;
  updated_at: string;
  sync_state: PendingMasterDataSyncState;
  last_error: string | null;
}

interface CachedDraftSummary {
  key: string;
  owner_user_id: number;
  kind: PendingMasterDataKind;
  value: unknown;
  updated_at: string;
}

const DB_NAME = "materialentnahme-master-data-drafts";
const DB_VERSION = 1;
const PENDING_STORE = "pending_v1";
const SUMMARY_STORE = "summary_v1";

function normalizeStorageLocation(value: unknown): string {
  return String(value ?? "").trim().toUpperCase().replace(/[\s\-_/]+/g, "");
}

function normalizeStorageLocations(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : typeof value === "string" ? value.split(/[;,|]/) : [];
  const result: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const normalized = normalizeStorageLocation(item);
    if (normalized && !seen.has(normalized)) { seen.add(normalized); result.push(normalized); }
  }
  return result;
}

function normalizeCabinet(value: unknown): string {
  const text = String(value ?? "").trim();
  if (!text) return "";
  if (/^\d+(?:\.0+)?$/.test(text)) {
    const number = Math.trunc(Number(text));
    return number < 1000 ? String(number).padStart(3, "0") : String(number);
  }
  const digits = text.replace(/\D/g, "");
  if (digits && digits.length <= 3) return digits.padStart(3, "0");
  return text.toUpperCase();
}

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("");
}

function plainObjectRecord(value: unknown): Record<string, unknown> | null {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return null;
  return Object.fromEntries(Object.entries(value));
}

async function canonicalPayload(kind: PendingMasterDataKind, value: unknown): Promise<Record<string, unknown> | null> {
  const record = plainObjectRecord(value);
  if (!record) return null;
  const source = record;
  if (kind === "material") {
    return {
      material_number: String(source.material_number ?? "").trim(),
      description: String(source.description ?? "").trim(),
      material_type: String(source.material_type ?? "").trim(),
      manufacturer: String(source.manufacturer ?? "").trim(),
      storage_locations: normalizeStorageLocations(source.storage_locations),
    };
  }
  if (kind === "allocation") {
    const orderNumber = String(source.order_number ?? "").replace(/\s/g, "").trim();
    const plant = String(source.plant ?? "").trim();
    const plantArea = String(source.plant_area ?? "").trim();
    const manufacturer = String(source.manufacturer ?? "").trim();
    const identity = manufacturer ? [orderNumber, plant, plantArea, manufacturer] : [orderNumber, plant, plantArea];
    const entryKey = await sha256Hex(JSON.stringify(identity));
    return { entry_key: entryKey, order_number: orderNumber, plant, plant_area: plantArea, manufacturer };
  }
  const location = String(source.location_code ?? "").replace(/\s+/g, "").toUpperCase().trim();
  const cabinet = normalizeCabinet(source.cabinet_number);
  return {
    key: `${location}|${cabinet}`,
    location_code: location,
    cabinet_number: cabinet,
    area: String(source.area ?? "").trim(),
    content: String(source.content ?? "").trim(),
    responsibility: String(source.responsibility ?? "").trim(),
  };
}

function currentOwnerId(): number | null {
  return getLastKnownAuthUser()?.id ?? null;
}

function recordKey(ownerUserId: number, kind: PendingMasterDataKind, identityKey: string): string {
  return `${ownerUserId}:${kind}:${identityKey}`;
}

function summaryKey(ownerUserId: number, kind: PendingMasterDataKind): string {
  return `${ownerUserId}:${kind}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PENDING_STORE)) {
        const store = db.createObjectStore(PENDING_STORE, { keyPath: "key" });
        store.createIndex("owner_kind", ["owner_user_id", "kind"], { unique: false });
        store.createIndex("owner_user_id", "owner_user_id", { unique: false });
      }
      if (!db.objectStoreNames.contains(SUMMARY_STORE)) {
        const store = db.createObjectStore(SUMMARY_STORE, { keyPath: "key" });
        store.createIndex("owner_kind", ["owner_user_id", "kind"], { unique: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Lokaler Stammdaten-Entwurfsspeicher konnte nicht geöffnet werden."));
  });
}

async function withDb<T>(run: (db: IDBDatabase) => Promise<T>): Promise<T> {
  if (!("indexedDB" in globalThis)) throw new Error("IndexedDB ist nicht verfügbar.");
  const db = await openDb();
  try { return await run(db); }
  finally { db.close(); }
}

async function getByKey(key: string): Promise<PendingMasterDataMutation | null> {
  return withDb((db) => new Promise((resolve, reject) => {
    const request = db.transaction(PENDING_STORE, "readonly").objectStore(PENDING_STORE).get(key);
    request.onsuccess = () => resolve((request.result as PendingMasterDataMutation | undefined) ?? null);
    request.onerror = () => reject(request.error);
  }));
}

async function putMutation(value: PendingMasterDataMutation): Promise<void> {
  await withDb((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PENDING_STORE, "readwrite");
    tx.objectStore(PENDING_STORE).put(value);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

export async function queuePendingMasterDataMutation(input: {
  kind: PendingMasterDataKind;
  identityKey: string;
  target: string | number | null;
  operation: PendingMasterDataOperation;
  payload?: unknown;
  baselineData?: unknown;
  draftRevision?: number | null;
  masterVersion?: number | null;
}): Promise<PendingMasterDataMutation | null> {
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) throw new Error("Stammdatenänderung kann ohne angemeldeten Benutzer nicht lokal gespeichert werden.");
  const canonical = await canonicalPayload(input.kind, input.payload ?? null);
  const canonicalBaseline = await canonicalPayload(input.kind, input.baselineData ?? null);
  const generatedIdentity = input.kind === "material"
    ? String(canonical?.material_number ?? "")
    : input.kind === "allocation"
      ? String(canonical?.entry_key ?? "")
      : String(canonical?.key ?? "");
  const identityKey = (input.identityKey.trim() || generatedIdentity).trim();
  if (!identityKey) throw new Error("Lokale Stammdatenänderung besitzt keinen eindeutigen Schlüssel.");
  const key = recordKey(ownerUserId, input.kind, identityKey);
  const existing = await getByKey(key);

  // Lokal neu angelegt und vor dem ersten Server-Sync wieder gelöscht: Es gibt
  // serverseitig nichts mehr zu tun. Den lokalen Entwurf vollständig entfernen.
  if (existing?.operation === "created" && input.operation === "deleted") {
    await deletePendingMasterDataMutation(key);
    return null;
  }

  const now = new Date().toISOString();
  const effectiveOperation: PendingMasterDataOperation =
    existing?.operation === "created" && input.operation === "updated" ? "created" : input.operation;
  const value: PendingMasterDataMutation = {
    key,
    owner_user_id: ownerUserId,
    kind: input.kind,
    identity_key: identityKey,
    target: existing?.target ?? input.target,
    operation: effectiveOperation,
    payload: input.operation === "deleted" ? null : (canonical ?? existing?.payload ?? null),
    baseline_data: existing?.baseline_data ?? canonicalBaseline ?? null,
    // Immer die erste bekannte Server-Precondition behalten. Mehrere lokale
    // Änderungen dürfen die CAS-Basis nicht künstlich nach vorne schieben.
    draft_revision: existing?.draft_revision ?? input.draftRevision ?? null,
    master_version: existing?.master_version ?? input.masterVersion ?? null,
    created_at: existing?.created_at ?? now,
    updated_at: now,
    sync_state: "pending",
    last_error: null,
  };
  await putMutation(value);
  window.dispatchEvent(new CustomEvent("materialentnahme:master-data-drafts-updated", { detail: { kind: input.kind } }));
  window.dispatchEvent(new Event("materialentnahme:status-refresh"));
  return value;
}

export async function getPendingMasterDataMutations(kind?: PendingMasterDataKind): Promise<PendingMasterDataMutation[]> {
  if (!("indexedDB" in globalThis)) return [];
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) return [];
  return withDb((db) => new Promise((resolve, reject) => {
    const store = db.transaction(PENDING_STORE, "readonly").objectStore(PENDING_STORE);
    const request = kind
      ? store.index("owner_kind").getAll(IDBKeyRange.only([ownerUserId, kind]))
      : store.index("owner_user_id").getAll(IDBKeyRange.only(ownerUserId));
    request.onsuccess = () => resolve(((request.result as PendingMasterDataMutation[] | undefined) ?? []).sort((a, b) => a.created_at.localeCompare(b.created_at)));
    request.onerror = () => reject(request.error);
  }));
}

export async function deletePendingMasterDataMutation(key: string): Promise<void> {
  if (!("indexedDB" in globalThis)) return;
  const ownerUserId = currentOwnerId();
  if (!ownerUserId || !key.startsWith(`${ownerUserId}:`)) return;
  await withDb((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(PENDING_STORE, "readwrite");
    tx.objectStore(PENDING_STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

export async function updatePendingMasterDataMutationState(key: string, state: PendingMasterDataSyncState, error: string | null): Promise<void> {
  const current = await getByKey(key);
  if (!current) return;
  await putMutation({ ...current, sync_state: state, last_error: error, updated_at: new Date().toISOString() });
}

export async function cacheMasterDataDraftSummaryIndexedDb(kind: PendingMasterDataKind, value: unknown): Promise<void> {
  const ownerUserId = currentOwnerId();
  if (!ownerUserId || !("indexedDB" in globalThis)) return;
  const row: CachedDraftSummary = { key: summaryKey(ownerUserId, kind), owner_user_id: ownerUserId, kind, value, updated_at: new Date().toISOString() };
  await withDb((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SUMMARY_STORE, "readwrite");
    tx.objectStore(SUMMARY_STORE).put(row);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

export async function readMasterDataDraftSummaryIndexedDb<T>(kind: PendingMasterDataKind): Promise<T | null> {
  if (!("indexedDB" in globalThis)) return null;
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) return null;
  return withDb((db) => new Promise((resolve, reject) => {
    const request = db.transaction(SUMMARY_STORE, "readonly").objectStore(SUMMARY_STORE).get(summaryKey(ownerUserId, kind));
    request.onsuccess = () => resolve(((request.result as CachedDraftSummary | undefined)?.value as T | undefined) ?? null);
    request.onerror = () => reject(request.error);
  }));
}

export async function clearMasterDataDraftSummaryIndexedDb(kind: PendingMasterDataKind): Promise<void> {
  if (!("indexedDB" in globalThis)) return;
  const ownerUserId = currentOwnerId();
  if (!ownerUserId) return;
  await withDb((db) => new Promise<void>((resolve, reject) => {
    const tx = db.transaction(SUMMARY_STORE, "readwrite");
    tx.objectStore(SUMMARY_STORE).delete(summaryKey(ownerUserId, kind));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}
